import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { endOfDay, startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Icon from 'react-feather';
import * as Yup from 'yup';

import CallexLoader from '../../../components/CallexLoader';
import FormSelect from '../../../components/form/FormSelect';
import {
  checkModulePermission,
  fileDownload,
  formatArchiveOptions,
  formatPayloadDate,
  getDateRangeFromLabel,
  zipFileDownload
} from '../../../utils/common';
import AdminHeader from '../../../components/header/AdminHeader';
import FormDateInput from '../../../components/form/FormDateInput';

import {
  archiveOptionsGet,
  processAllOptionGet,
  recordingReportDownload,
  recordingReportGet
} from '../../../store/slices/tenantSlices/dailyReportSlice';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import { dispositionOptionsGet } from '../../../store/slices/tenantSlices/settingSlice';
import { userOptionGetByProcessId } from '../../../store/slices/callingSlice';
import FormInput from '../../../components/form/FormInput';
import AudioPlayerModal from '../../../components/Modals/AudioPlayerModal';
import Pagination from '../../../components/form/Pagination';
import config from '../../../config';
import { useTimezone } from '../../../store/slices/commonSlice';
import { toZonedTime } from 'date-fns-tz';

const RecordingReport = () => {
  const dispatch = useDispatch();
  const timezone = useTimezone();
  const userRolePermission = useUserRolePermission();
  const [paginationDetails, setPaginationDetails] = useState({ page: 1, total: 0, limit: 10 });
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  // Starts true because the first search waits for the archive options to resolve -
  // without this the empty state flashes before the loader appears.
  const [isLoading, setIsLoading] = useState(true);
  const [reportData, setReportData] = useState([]);
  const [processOptions, setProcessOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [dispositionOptions, setDispositionOptions] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isCsvDownload, setIsCsvDownload] = useState(false);
  const [csvReportData, setCsvReportData] = useState([]);
  const [csvHeaderData, setCsvHeaderData] = useState([]);
  const [audioDetails, setAudioDetails] = useState(null);
  const [archiveOptions, setArchiveOptions] = useState([]);
  const hasAutoSearchedRef = useRef(false);

  const today = toZonedTime(new Date(), timezone);
  const initialValues = {
    page: 1,
    limit: 10,
    start_date: startOfDay(today),
    end_date: endOfDay(today),
    processId: 'All',
    userId: 'All',
    disposition: 'All',
    crm_number: '',
    // partner_name: '',
    archiveId: 0
  };

  const validationSchema = Yup.object({
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date().required('End date is required')
  });

  // `range` lets the first automatic search use the month range as soon as it is
  // derived, without waiting for setFieldValue to land in `values`.
  const fetchReport = async (range) => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const formattedStartDate = formatPayloadDate(range?.start_date || values.start_date);
      const formattedEndDate = formatPayloadDate(range?.end_date || values.end_date);

      const payload = {
        ...values,
        processId: values?.processId || 'All',
        userId: values?.userId || 'All',
        disposition: values?.disposition || 'All',
        crm_number: values?.crm_number || undefined,
        // partner_name: values?.partner_name?.trim() || undefined
      };
      payload['start_date'] = formattedStartDate;
      payload['end_date'] = formattedEndDate;

      const response = await dispatch(recordingReportGet(payload)).unwrap();
      if (response) {
        setReportData(response);
        setPaginationDetails({
          ...paginationDetails,
          limit: response.limit,
          page: response.page,
          total: response.total
        });
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const onSubmit = () => fetchReport();

  // Runs the load-time search exactly once, whichever path resolves the range first.
  const runInitialSearch = (range) => {
    if (hasAutoSearchedRef.current) return;
    hasAutoSearchedRef.current = true;
    fetchReport(range);
  };

  const { values, handleBlur, setFieldValue, handleSubmit, handleChange, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  useEffect(() => {
    // Skip the mount pass - the initial search is fired once the archive-derived
    // range is known, so it matches the dates shown in the filter. Page/limit
    // changes after that keep paginating as before.
    if (!hasAutoSearchedRef.current) return;
    onSubmit();
  }, [values?.limit, values?.page]);

  const getProcessOption = async () => {
    try {
      const response = await dispatch(processAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  const getDispositionOptions = async () => {
    try {
      const response = await dispatch(
        dispositionOptionsGet({ process_id: values?.processId })
      ).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.DisposName,
          value: val.DisposName
        }));
        setDispositionOptions(options);
      } else {
        setDispositionOptions([]);
      }
    } catch (error) {
      setDispositionOptions([]);
      console.log('error', error);
    }
  };

  const fetchUserOptionByProcessId = async () => {
    try {
      const response = await dispatch(
        userOptionGetByProcessId({ process_id: values?.processId })
      ).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.Username || '-',
          value: val.id
        }));
        setUserOptions(options);
      } else {
        setUserOptions([]);
      }
    } catch (error) {
      setUserOptions([]);
      console.error('Error fetching user options:', error);
    }
  };

  const getArchiveOptions = async () => {
    try {
      const response = await dispatch(archiveOptionsGet()).unwrap();
      if (response && response.data) {
        const options = formatArchiveOptions(response.data, timezone);
        setArchiveOptions(options);
      } else {
        setArchiveOptions([]);
        // No month options means no derived range, so fall back to the default dates.
        runInitialSearch();
      }
    } catch (error) {
      setArchiveOptions([]);
      runInitialSearch();
      console.log('error', error);
    }
  };

  useEffect(() => {
    getArchiveOptions();
    getProcessOption();
  }, []);

  useEffect(() => {
    if (values?.processId !== 'All') {
      fetchUserOptionByProcessId();
      getDispositionOptions();
    } else {
      setUserOptions([]);
      setDispositionOptions([]);
      setFieldValue('disposition', 'All');
      setFieldValue('userId', 'All');
    }
  }, [values?.processId]);

  const prepareExportProps = () => {
    const data = csvReportData || [];
    return { headers: csvHeaderData, data, filename: 'Recording_report.csv' };
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);

      const payload = {
        ...values,
        processId: values?.processId || 'All',
        userId: values?.userId || 'All',
        disposition: values?.disposition || 'All',
        crm_number: values?.crm_number || undefined,
        // partner_name: values?.partner_name?.trim() || undefined
      };
      payload['start_date'] = formattedStartDate;
      payload['end_date'] = formattedEndDate;

      delete payload['limit'];
      delete payload['page'];

      const response = await dispatch(recordingReportGet(payload)).unwrap();
      const headers = response?.header.map((val) => ({ label: val?.label, key: val?.name }));
      setCsvReportData(response?.data || []);
      setCsvHeaderData(headers);
      setIsExporting(false);
      setIsCsvDownload(true);
    } catch (error) {
      setIsExporting(false);
      console.log('Error exporting CSV:', error);
    }
  };

  const onZipDownload = async () => {
    try {
      const formattedStartDate = formatPayloadDate(values.start_date);
      const formattedEndDate = formatPayloadDate(values.end_date);

      const payload = {
        ...values,
        processId: values?.processId || 'All',
        userId: values?.userId || 'All',
        disposition: values?.disposition || 'All',
        crm_number: values?.crm_number || undefined,
        // partner_name: values?.partner_name?.trim() || undefined
      };
      payload['start_date'] = formattedStartDate;
      payload['end_date'] = formattedEndDate;

      delete payload['limit'];
      delete payload['page'];

      const response = await dispatch(recordingReportDownload(payload)).unwrap();
      if (response) {
        const fileName = `recordings_${formattedStartDate}_to_${formattedEndDate}.zip`;
        zipFileDownload(response, fileName);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const selectedOption = useMemo(
    () => archiveOptions.find((opt) => opt.value === values.archiveId),
    [values.archiveId, archiveOptions]
  );

  const selectedLabel = selectedOption?.label || null;

  const { minDate, maxDate } = useMemo(() => {
    return getDateRangeFromLabel(selectedLabel, timezone);
  }, [selectedLabel, timezone]);

  // The archive options land after mount, so the month range is only known here.
  // The first search has to wait for it, otherwise it fetches today's range while
  // the filter already shows the whole month.
  useEffect(() => {
    if (minDate && maxDate) {
      setFieldValue('start_date', minDate);
      setFieldValue('end_date', maxDate);
      runInitialSearch({ start_date: minDate, end_date: maxDate });
    } else if (archiveOptions?.length > 0) {
      // Options loaded but the label gave no range - do not leave the first search pending.
      runInitialSearch();
    }
  }, [minDate?.getTime(), maxDate?.getTime(), archiveOptions?.length]);

  return (
    <div>
      <AdminHeader
        title="Recording Report"
        isSearch={true}
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isExportDisabled={
          !checkModulePermission('is_export', 'Recording Report', userRolePermission)
        }
        csvProps={prepareExportProps()}
        isExport={reportData?.data?.length > 0}
        onExportClick={() => handleExportCSV()}
        setIsCsvDownload={setIsCsvDownload}
        isCsvDownload={isCsvDownload}
        isExporting={isExporting}
        isRefresh={reportData?.data?.length > 0}
        onRefreshClick={() => onSubmit()}
        isZipDownload={reportData?.data?.length > 0}
        onZipDownload={() => onZipDownload()}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="Month"
                  placeholder="Select month"
                  value={values.archiveId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="archiveId"
                  options={archiveOptions}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="start_date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  label="From Date"
                  showTimeSelect={true}
                  minDate={minDate}
                  maxDate={values.end_date || maxDate}
                  value={values.start_date}
                  onChange={(e) => setFieldValue('start_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormDateInput
                  name="end_date"
                  label="To Date"
                  dateFormat="yyyy-MM-dd HH:mm:ss"
                  showTimeSelect={true}
                  minDate={values.start_date || minDate}
                  maxDate={maxDate}
                  value={values.end_date}
                  onChange={(e) => setFieldValue('end_date', e.target.value)}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="Process"
                  placeholder="Select Process"
                  value={values.processId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="processId"
                  options={
                    processOptions?.length > 0
                      ? [
                        {
                          label: 'All',
                          value: 'All'
                        },
                        ...processOptions
                      ]
                      : []
                  }
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="User"
                  placeholder="Select User"
                  value={values.userId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="userId"
                  options={
                    userOptions?.length > 0
                      ? [
                        {
                          label: 'All',
                          value: 'All'
                        },
                        ...userOptions
                      ]
                      : []
                  }
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormSelect
                  label="Disposition"
                  placeholder="Select Disposition"
                  value={values.disposition}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="disposition"
                  options={
                    dispositionOptions?.length > 0
                      ? [
                        {
                          label: 'All',
                          value: 'All'
                        },
                        ...dispositionOptions
                      ]
                      : []
                  }
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="3" className="flex-row">
                <FormInput
                  label="Number"
                  placeholder="Enter Number"
                  value={values.crm_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="crm_number"
                  touched={touched}
                  errors={errors}
                />
              </Col>
              {/* <Col lg="3" md="3" className="flex-row">
                <FormInput
                  label="Partner Name"
                  placeholder="Enter Partner Name"
                  value={values.partner_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="partner_name"
                  touched={touched}
                  errors={errors}
                />
              </Col> */}
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 410px)' : 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData.data?.length > 0 ? (
        <Card className="p-0 position-relative mb-0">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 450px)' : 'calc(100vh - 208px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  {reportData?.header?.map((val, index) => {
                    return <th key={index}>{val.label}</th>;
                  })}
                </tr>
              </thead>
              <tbody>
                {reportData.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      {reportData?.header?.map((value, idx) => {
                        if (value?.name === 'recording_file') {
                          return (
                            <td key={idx} className="text-center cursor-pointer">
                              {val[value?.name] && val[value?.name] !== '-' ? (
                                <div className="d-flex gap-2">
                                  <Icon.PlayCircle
                                    size={16}
                                    onClick={() =>
                                      setAudioDetails({
                                        ...val,
                                        src: `${config.BASE_URL}cdrs_report/download?filename=${val.recording_file}`
                                      })
                                    }
                                    className="cursor-pointer text-info"
                                  />
                                  <Icon.Download
                                    size={16}
                                    onClick={() => {
                                      fileDownload(
                                        `${config.BASE_URL}cdrs_report/download?filename=${val.recording_file}`
                                      );
                                    }}
                                  />
                                </div>
                              ) : (
                                '-'
                              )}
                            </td>
                          );
                        }
                        return <td key={idx}>{val[value?.name] || '-'}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={paginationDetails?.total}
              limit={paginationDetails?.limit}
              page={paginationDetails?.page}
              onLimitChange={(val) => {
                setFieldValue('limit', val);
                setFieldValue('page', 1);
              }}
              onPageClick={(val) => setFieldValue('page', val)}
              lastPageClick={(val) => setFieldValue('page', val)}
              firstPageClick={(val) => setFieldValue('page', val)}
              onRefreshClick={() => onSubmit()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 410px)' : 'calc(100vh - 165px)' }}>
          No data available
        </Card>
      )}
      {audioDetails && (
        <AudioPlayerModal
          modalOpen={!!audioDetails}
          handleClose={() => setAudioDetails(null)}
          details={audioDetails}
        />
      )}
    </div>
  );
};

export default RecordingReport;
